import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function PreviewList({ title, items, testid, muted }) {
    if (!items.length) return null;
    return (
        <div data-testid={testid}>
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{title} ({items.length})</p>
            <div className="max-h-36 overflow-y-auto rounded border border-border/50 divide-y divide-border/30 text-xs">
                {items.map((x, i) => (
                    <div key={i} className={`flex justify-between px-2 py-1 font-mono ${muted ? 'text-muted-foreground' : ''}`}>
                        <span>{x.date} · {x.flight_number}</span><span>{x.origin}→{x.destination} {x.departure}{x.arrives_next_day ? ' (+1)' : ''}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function GenerateFlightsDialog({ open, onOpenChange, schedules, headers, onDone }) {
    const [mode, setMode] = useState('range');
    const [form, setForm] = useState({ date_from: today(), date_to: today(), schedule_id: 'all' });
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const body = () => ({
        date_from: form.date_from, date_to: mode === 'day' ? form.date_from : form.date_to,
        ...(form.schedule_id !== 'all' ? { schedule_id: form.schedule_id } : {}),
    });
    const set = (patch) => { setForm(f => ({ ...f, ...patch })); setPreview(null); };
    const run = async (isPreview) => {
        if (!form.date_from || (mode === 'range' && !form.date_to)) { toast.error('Selecciona las fechas'); return; }
        setBusy(true);
        try {
            const r = await axios.post(`${API}/schedules/generate`, { ...body(), preview: isPreview }, { headers });
            if (isPreview) { setPreview(r.data); return; }
            toast.success(`${r.data.created} vuelos generados, ${r.data.skipped} ya existían`);
            setPreview(null);
            onOpenChange(false);
            onDone?.();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'No se pudo generar');
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setPreview(null); }}>
            <DialogContent className="sm:max-w-[520px]" data-testid="generate-dialog">
                <DialogHeader>
                    <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">Generar Vuelos</DialogTitle>
                    <DialogDescription>Crea vuelos a partir de las programaciones activas. Revisa la vista previa antes de confirmar; los vuelos que ya existen no se duplican.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Programación</Label>
                            <Select value={form.schedule_id} onValueChange={v => set({ schedule_id: v })}>
                                <SelectTrigger data-testid="gen-schedule"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas las rutas</SelectItem>
                                    {schedules.filter(s => s.active !== false).map(s => (
                                        <SelectItem key={s.id} value={s.id}>{s.flight_number} · {s.origin}→{s.destination}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Periodo</Label>
                            <Select value={mode} onValueChange={v => { setMode(v); setPreview(null); }}>
                                <SelectTrigger data-testid="gen-mode"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="day">Un solo día</SelectItem>
                                    <SelectItem value="range">Rango de fechas</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>{mode === 'day' ? 'Día' : 'Desde'}</Label>
                            <Input type="date" value={form.date_from} onChange={e => set({ date_from: e.target.value })} className="input-aviation font-mono" data-testid="gen-from" />
                        </div>
                        {mode === 'range' && (
                            <div className="space-y-1.5">
                                <Label>Hasta</Label>
                                <Input type="date" value={form.date_to} onChange={e => set({ date_to: e.target.value })} className="input-aviation font-mono" data-testid="gen-to" />
                            </div>
                        )}
                    </div>
                    {preview && (
                        <div className="space-y-2 rounded-md border border-sky-500/30 bg-sky-500/5 p-3" data-testid="gen-preview">
                            <div className="flex flex-wrap gap-2 text-xs">
                                <Badge className="bg-emerald-500/15 text-emerald-400" data-testid="gen-preview-create">Se crearán: {preview.created}</Badge>
                                <Badge variant="outline" data-testid="gen-preview-existing">Ya existen: {preview.skipped}</Badge>
                                {preview.dates?.length > 0 && <Badge variant="outline">Fechas: {preview.dates[0]}{preview.dates.length > 1 ? ` a ${preview.dates[preview.dates.length - 1]}` : ''}</Badge>}
                            </div>
                            {preview.truncated_by_quota && <p className="text-xs text-amber-400">El límite mensual de vuelos del plan recorta la generación.</p>}
                            {preview.created === 0 && <p className="text-xs text-muted-foreground" data-testid="gen-preview-empty">No hay vuelos nuevos que crear con estos filtros.</p>}
                            <PreviewList title="Se crearán" items={preview.to_create} testid="gen-preview-create-list" />
                            <PreviewList title="Ya existen (no se duplican)" items={preview.existing} testid="gen-preview-existing-list" muted />
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                    {!preview ? (
                        <Button onClick={() => run(true)} disabled={busy} className="btn-primary" data-testid="gen-preview-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}Vista previa
                        </Button>
                    ) : (
                        <Button onClick={() => run(false)} disabled={busy || preview.created === 0} className="btn-primary" data-testid="submit-generate">
                            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Confirmar y generar {preview.created}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
